import { ChangeEvent, FC, lazy, LazyExoticComponent, Suspense, useMemo, useState } from 'react';

import FrontImage from './assets/Front.png';
import BackImage from './assets/Back.png';

import { Typography, Input, Button, Flex, Image, Spin } from 'antd';

import { CUSTOMIZATION_FIELDS } from './constants';
import {
  IAlterationDetails,
  IconPropsType,
  IOrderLine,
  OrderLineBrandStatus,
} from '@admin-panel/types';

import * as Styles from './styles';
import { useCustomMutation, useUpdate } from '@refinedev/core';
import { CustomizationCategoryType } from '@admin-panel/types';

const { TextArea } = Input;

const { Text } = Typography;

type DiagramComponent = FC<IconPropsType> | LazyExoticComponent<FC<IconPropsType>>;

const TopLabels = lazy<FC<IconPropsType>>(() => import('./assets/TopLabels'));
const PantLabels = lazy<FC<IconPropsType>>(() => import('./assets/PantLabels'));
const CoOrdSetLabels = lazy<FC<IconPropsType>>(() => import('./assets/CoOrdSetLabels'));
const DressLabels = lazy<FC<IconPropsType>>(() => import('./assets/DressLabels'));
const EmptyDiagram: FC<IconPropsType> = (_props: IconPropsType) => null;

export const DIAGRAM_IMAGE: Record<CustomizationCategoryType, DiagramComponent> = {
  TOP_WEAR: TopLabels,
  BOTTOM_WEAR: PantLabels,
  CO_ORD_SET: CoOrdSetLabels,
  DRESS: DressLabels,
  NONE: EmptyDiagram,
} as const;

export interface IPersonalizationProps {
  lineItemId: number;
  alterationEntry: IAlterationDetails;
  allowCustomizationEdit: boolean;
  onSuccess?: (args?: any) => void;
}

const ItemCustomizationForm = ({
  lineItemId,
  alterationEntry,
  allowCustomizationEdit,
  onSuccess,
}: IPersonalizationProps) => {
  const [formData, setFormData] = useState<IAlterationDetails>(alterationEntry);
  const [actionInProgress, setActionInProgress] = useState(false);

  const formSections = useMemo(() => {
    const sections = [];

    if (alterationEntry.customizationCategory === 'DRESS') {
      sections.push(CUSTOMIZATION_FIELDS.DRESS);
      return sections;
    }

    if (alterationEntry.topwearAlterationEntry) {
      sections.push(CUSTOMIZATION_FIELDS.TOP_WEAR);
    }

    if (alterationEntry.bottomwearAlterationEntry) {
      sections.push(CUSTOMIZATION_FIELDS.BOTTOM_WEAR);
    }
    return sections;
  }, []);

  type FieldName =
    | keyof IAlterationDetails['bottomwearAlterationEntry']
    | keyof IAlterationDetails['topwearAlterationEntry'];

  const { mutate: confirmCustomizationMutation } = useCustomMutation<IOrderLine>();

  const saveAndConfirmCustomizations = () => {
    console.log('Save Form', formData);

    setActionInProgress(true);

    const payload: any = { alterationEntry: formData };

    confirmCustomizationMutation(
      {
        url: '',
        method: 'put',
        meta: {
          resource: 'lineitems',
          action: 'performItemAction',
          itemId: lineItemId,
          actionCode: 'customization_review_done',
        },
        values: payload,
      },
      {
        onError: (error, variables, context) => {
          // An error occurred!
          setActionInProgress(false);
        },
        onSuccess: (data, variables, context) => {
          onSuccess?.();
        },
      },
    );
  };

  const DiagramImage = alterationEntry.customizationCategory
    ? DIAGRAM_IMAGE[alterationEntry.customizationCategory as keyof typeof DIAGRAM_IMAGE]
    : null;

  return (
    <Styles.Wrapper>
      <Styles.Left>
        {formSections.map(section => {
          return (
            <Styles.FormSectionContainer key={section.title}>
              <Text>{section.title}</Text>
              <Styles.FormSection>
                {section.fields.map(field => (
                  <Flex vertical gap={4}>
                    <Typography.Text style={{ fontSize: 12, opacity: 0.5 }}>
                      {field.label} (inch)
                    </Typography.Text>
                    <Input
                      disabled={!allowCustomizationEdit}
                      key={field.name}
                      placeholder={field.label}
                      name={field.name}
                      type="number"
                      value={formData?.[section.accessor]?.[field.name as FieldName] ?? ''}
                      onChange={e =>
                        setFormData(prev => ({
                          ...prev,
                          [section.accessor]: {
                            ...prev[section.accessor],
                            [field.name]: e.target.value,
                          },
                        }))
                      }
                      min={0}
                    />
                  </Flex>
                ))}
              </Styles.FormSection>
            </Styles.FormSectionContainer>
          );
        })}
        <Flex vertical gap={4}>
          <Typography.Text style={{ fontSize: 12, opacity: 0.5 }}>Remarks</Typography.Text>
          <TextArea
            disabled={!allowCustomizationEdit}
            placeholder="Remarks"
            value={formData.remarks}
            onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
              setFormData(prev => ({ ...prev, remarks: e.target.value }))
            }
          />
        </Flex>
        {allowCustomizationEdit && (
          <Button
            type="primary"
            onClick={saveAndConfirmCustomizations}
            style={{ width: 150 }}
            loading={actionInProgress}
          >
            Save and Confirm
          </Button>
        )}
      </Styles.Left>

      <Styles.Right>
        <Suspense fallback={<Spin />}>
          {DiagramImage ? (
            <DiagramImage />
          ) : (
            <div
              style={{
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              No diagram available
            </div>
          )}
        </Suspense>
      </Styles.Right>
    </Styles.Wrapper>
  );
};

export default ItemCustomizationForm;
